<script lang="ts">
  import {checked} from './face-tracker-store';
  import {getInstance} from '../../services/animation';
  import FaceTrackingService from '../../services/face-tracking';
  import {faceTrackingStore, preview, project, draftStore} from '../../store';
  import Swal from 'sweetalert2';
  import {appEvents, DraftService} from '../../services';
  import editorStore from '../../lib/editor/editor.store';
  import {onDestroy, onMount} from 'svelte';
  import App from '../../App.svelte';
  import {logError, logWarning} from '../../utils/logs';
  import {topnavLockPrevious, topnavUnlockPrevious} from '../../platform-redesign-components/TopNavNext.svelte';
  import {isLowResolutionPreview, selectBestRenderedVideo} from '../../pages/preview/PreviewArea.svelte';
  import tippy from 'tippy.js';
  let currentSrc = '';
  let processing = false;
  let checkbox: HTMLElement;

  const faceTrackingService = new FaceTrackingService();

  function showSwalError() {
    faceTrackingStore.set({loading: false});
    Swal.fire({
      title: 'AI Camera Crew Error!',
      text: 'Could not start AI Camera Crew please try again in a few seconds. If the problem persists please contact support.',
      icon: 'error',
      confirmButtonText: 'Ok'
    });
  }

  function onRenderRequestCompleted(resp: any) {
    // Implement tracking
  }

  async function executeV2Tracking() {
    faceTrackingStore.update(store => ({
      ...store,
      loading: true,
      trackingProgress: 0
      // faceSelection: true
    }));

    const src = $project.compiledVideo?.cloudLocation;
    topnavLockPrevious(
      'Please wait for the current AI Camera Crew process to finish before moving to a previous step.'
    );

    faceTrackingService
      .createRenderRequestv2($draftStore.currentCloudDraftId as number)
      .then(resp => {
        console.log('resp', resp);
        onRenderRequestCompleted(resp);
      })
      .catch(err => {
        logError(err);
        showSwalError();
      });
    return true;
  }

  async function track() {
    if ($faceTrackingStore.v2) {
      console.info('Using V2 Tracking');
      return await executeV2Tracking();
    }
    faceTrackingStore.update(store => ({
      ...store,
      loading: true,
      trackingProgress: 0,
      compileProgress: 0
    }));

    const src = $project.compiledVideo?.cloudLocation;
    if (src) {
      topnavLockPrevious(
        'Please wait for the current AI Camera Crew process to finish before moving to a previous step.'
      );
      faceTrackingService
        .createRenderRequest($draftStore.currentCloudDraftId as string, src)
        .then(resp => {
          onRenderRequestCompleted(resp);
        })
        .catch(err => {
          logError(err);
          showSwalError();
        });
    } else {
      // Problem with the dom probablyuses
      showSwalError();
    }
  }

  function hasExistingAICameraCrewVideo(): boolean {
    return $project.compiledAICameraCrewVideoV2 ? true : false;
  }

  async function loadExistingVideo() {
    let url;
    if ($faceTrackingStore.v2) {
      url = $project.compiledAICameraCrewVideoV2.publicUrl;
    } else {
      url = $project.compiledAICameraCrewVideo.publicUrl;
    }
    if (!url) throw new Error('No existing video found');
    await getInstance().attachVideoObject(url);
    getInstance().prepareAICC();
    await getInstance().buildVideoDom();
    resetSeek();
    return true;
  }

  async function startTracking(retries = 0, delay = 1000): Promise<void> {
    try {
      if (hasExistingAICameraCrewVideo()) loadExistingVideo();
      else track();
    } catch (err) {
      if (retries < 3) {
        setTimeout(() => {
          startTracking(retries + 1, delay * 2);
        }, delay);
      } else {
        showSwalError();
        logError('AI Camera crew', err);
      }
    }
  }

  function enable() {
    startTracking();
    project.set({
      usesAICameraCrew: true
    });
  }

  function disable() {
    disableCameraCrew();
    project.set({
      usesAICameraCrew: false
    });
    processing = false;
  }

  function onCheckChange() {
    editorStore.setActiveComponent(null);
    processing = true;
    $checked ? enable() : disable();
  }

  function resetSeek() {
    getInstance().seek(0.01);
    window.dispatchEvent(new Event('reset-timeline'));
  }

  async function disableCameraCrew() {
    const draftService = new DraftService();
    const draft = await draftService.getDraft($draftStore.currentCloudDraftId as number);
    if (draft.data.renderedVideos.length === 0) {
      logWarning('No rendered videos found');
      return;
    }
    const video = selectBestRenderedVideo(draft.data.renderedVideos, false, false);
    if (video != null) {
      getInstance().attachVideoObject(video.file.publicUrl);
      getInstance().getSermonClipElement();
      resetSeek();
    }
  }

  async function attachVideo(videoUrl: string, hideAnnouncement = false) {
    resetSeek();
    await getInstance().attachVideoObject(videoUrl);
    getInstance().prepareAICC();
    await getInstance().buildVideoDom();
    getInstance().attachClassToSermonClip('ai-camera-crew');
    if (hideAnnouncement) return true;
    Swal.fire({
      title: 'AI Camera Crew Ready!',
      text: 'Our AI Camera Crew is now actively tracking your speaker. Play the video to see it in action.',
      icon: 'success',
      showConfirmButton: false,
      timer: 5000,
      toast: true,
      width: 500,
      position: 'bottom-right'
    });
  }

  function onVideoRendered(data: {
    event: string;
    renderedVideo: {
      cloudUrl: string;
      publicUrl: string;
      mediaLink: string;
      name: string;
      sourceFile: string;
    };
  }) {
    // if (
    //     data.renderedVideo.sourceFile !==
    //     $project.compiledVideo?.cloudLocation
    // )
    //     return;
    processing = false;
    faceTrackingStore.update(store => ({
      ...store,
      loading: false,
      trackingProgress: 0,
      compileProgress: 0
    }));
    attachVideo(data.renderedVideo.publicUrl);
    topnavUnlockPrevious();
    if ($faceTrackingStore.v2) {
      project.set({
        compiledAICameraCrewVideoV2: data.renderedVideo,
        usesAICameraCrewV2: true
      });
    } else {
      project.set({
        compiledAICameraCrewVideo: data.renderedVideo,
        usesAICameraCrew: true
      });
    }
  }

  onMount(() => {
    appEvents.on('video-rendered', onVideoRendered);

    if ($project.usesAICameraCrew || $project.usesAICameraCrewV2) {
      checked.set(true);
    } else {
      checked.set(false);
    }
  });

  onDestroy(() => {
    appEvents.off('video-rendered', onVideoRendered);
  });

  let tippyInstance: any = null;
  $: {
    if (tippyInstance) {
      try {
        tippyInstance.destroy();
        tippyInstance = null;
      } catch (err) {
        console.error('could not destroy tippy instance');
      }
    }
    if ($isLowResolutionPreview) {
      if (checkbox) {
        tippyInstance = tippy(checkbox, {
          content: 'Please wait for the final video to render before triggering AI Camera Crew',
          placement: 'right',
          theme: 'light'
        });
      }
    }
  }
</script>

{#if !$faceTrackingStore.loading}
  <div class="flex items-center" bind:this={checkbox}>
    <label class="relative inline-flex items-center {$faceTrackingStore.loading ? 'cursor-wait' : 'cursor-pointer'}">
      <input
        type="checkbox"
        value=""
        class="peer sr-only"
        disabled={$isLowResolutionPreview}
        bind:checked={$checked}
        on:change={onCheckChange}
      />
      <div
        class="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-secondary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-200 dark:border-gray-600 dark:bg-secondary dark:peer-focus:ring-secondary-600"
      />
    </label>
  </div>
{:else}
  <div class="block">
    <!-- <Icon name="Spinner" className=" text-gray-400  w-4 h-4 animate-spin inline-block mr-1" /> -->
    <!-- <span class="text-xs inline-block"> Processing video data... </span> -->
  </div>
{/if}
